import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import { DEFAULT_NOTE_COLORS, ENABLE_BULK_PERMANENT_DELETE } from '../constants';

const BulkActionBar = ({ isMobile }) => {
  const { 
    selectedNoteIds, setSelectedNoteIds, bulkArchive, 
    bulkAddTag, bulkRemoveTag, bulkSetColor, bulkDeletePermanently, notes 
  } = useStore();
  
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [tagName, setTagName] = useState('');

  if (selectedNoteIds.length === 0) return null;

  const selectedNotes = notes.filter(n => selectedNoteIds.includes(n.id));
  const commonTags = [...new Set(selectedNotes.flatMap(n => n.tags || []))];

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ y: 100, opacity: 0 }} 
        animate={{ y: 0, opacity: 1 }} 
        exit={{ y: 100, opacity: 0 }} 
        className={`fixed z-[9000] bg-gray-900 text-white shadow-2xl border border-white/10 flex items-center ${
          isMobile 
            ? 'bottom-[calc(16px+env(safe-area-inset-bottom))] left-3 right-3 min-h-16 px-3 py-2 rounded-2xl justify-between overflow-x-auto no-scrollbar' 
            : 'bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-2xl gap-6'
        }`}
      >
        <div className={`flex items-center gap-2 pr-4 ${!isMobile ? 'border-r border-white/20' : ''}`}>
          <span className="text-sm font-bold bg-blue-500 w-6 h-6 rounded-full flex items-center justify-center shrink-0">{selectedNoteIds.length}</span>
          {!isMobile && <span className="text-sm font-medium opacity-80">Selected</span>}
        </div>

        <div className="flex items-center gap-4 shrink-0">
          {!isMobile && (
            <div className="flex items-center gap-1.5">
              {DEFAULT_NOTE_COLORS.map(c => (
                <button key={c.key} onClick={() => bulkSetColor(selectedNoteIds, c.key)} className="w-5 h-5 rounded-full border border-white/20 hover:scale-110 transition" style={{ backgroundColor: c.hex }} title={`Set color: ${c.label}`} />
              ))}
            </div>
          )}
          
          {!isMobile && <div className="h-4 w-px bg-white/20" />}
          
          <div className="relative">
            <button onClick={() => setShowTagMenu(!showTagMenu)} className="min-w-11 min-h-11 px-2 flex items-center justify-center gap-2 text-sm hover:text-blue-400 transition" >
              <SafeIcon name="Tag" /> {!isMobile && 'Tags'}
            </button>
            {showTagMenu && (
              <div className={`absolute mb-4 bg-white text-gray-800 p-4 rounded-xl shadow-2xl border border-gray-100 min-w-[240px] ${isMobile ? 'bottom-full -left-12' : 'bottom-full left-0'}`}>
                <div className="mb-3">
                  <input autoFocus placeholder="Add new tag..." value={tagName} onChange={e => setTagName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && tagName) { bulkAddTag(selectedNoteIds, tagName); setTagName(''); setShowTagMenu(false); } }} className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                {commonTags.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Remove Tag</div>
                    <div className="flex flex-wrap gap-1">
                      {commonTags.map(tag => (
                        <button key={tag} onClick={() => { bulkRemoveTag(selectedNoteIds, tag); setShowTagMenu(false); }} className="px-2 py-1 bg-gray-100 hover:bg-red-50 hover:text-red-600 rounded text-xs transition flex items-center gap-1">
                          {tag} <SafeIcon name="X" className="w-2 h-2" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <button onClick={() => bulkArchive(selectedNoteIds)} className="flex items-center gap-2 text-sm hover:text-blue-400 transition" >
            <SafeIcon name="Archive" /> {!isMobile && 'Archive'}
          </button>

          {ENABLE_BULK_PERMANENT_DELETE && (
            <button onClick={() => bulkDeletePermanently(selectedNoteIds)} className="min-w-11 min-h-11 px-2 flex items-center justify-center gap-2 text-sm hover:text-red-400 transition" >
              <SafeIcon name="Trash2" /> {!isMobile && 'Delete'}
            </button>
          )}

          <div className="h-4 w-px bg-white/20" />
          
          <button onClick={() => setSelectedNoteIds([])} className="min-w-11 min-h-11 text-white/50 hover:text-white transition p-2 rounded-xl" >
            <SafeIcon name="X" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BulkActionBar;